import { FC, ReactNode, useState } from 'react';
import classNames from 'classnames';
import { MedusaProductVariant } from 'lib/home/generated/types/api';
import {
  AlertTextButton,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  MoreInfoTextButton,
  MealImage,
  CardTag
} from 'lib/components';
import { FavoriteButton, NutrientLabelModal } from '../';
import './meal-card.scss';
import { medusaPriceAmountToMoney } from 'lib/home/util/currency';
import { parseVariantMetadata } from '../../../../api-store/src/helpers/metadata';

export interface MealCardProps {
  onProductVariantUpdate?: (item: MedusaProductVariant) => void;
  actions?: ReactNode;
  alert?: boolean;
  className?: string;
  id?: string;
  meal: MedusaProductVariant;
  tags?: string[];
}

export const MealCard: FC<MealCardProps> = ({ onProductVariantUpdate, actions, alert, className, id, meal, tags }) => {
  const [nutrientLabelOpen, setNutrientLabelOpen] = useState(false);
  const { metadata, product, dietary_information, variant_preference } = meal || {};
  const { title, description, thumbnail } = product;
  const { never } = variant_preference || {};
  const isPremium = !!metadata.premium_upcharge;
  const { premium_upcharge } = parseVariantMetadata(metadata);

  return (
    <Card id={id} className={classNames('meal-card', className)} data-cy="meal-card">
      <CardHeader className="relative px-8 pt-8 pb-0">
        {never && <div className="bg-stone-700 text-white p-2 w-32 text-center absolute left-0 top-4">Never</div>}
        {!never && <FavoriteButton meal={meal} onProductVariantUpdate={onProductVariantUpdate} />}
        <MealImage url={thumbnail} title={title} />
        {(tags || isPremium) && (
          <div className="absolute right-4 top-4 flex flex-wrap justify-end gap-2 max-w-[240px]">
            {isPremium && <CardTag label="Premium" />}
            {tags && tags.map((tag, index) => <CardTag key={index} label={tag} />)}
          </div>
        )}
      </CardHeader>

      <CardContent className="flex flex-wrap items-center pt-2" variant="condensed">
        {alert ? (
          <AlertTextButton className="text-alert" onClick={() => setNutrientLabelOpen(true)}>
            Contains Allergens
          </AlertTextButton>
        ) : (
          <MoreInfoTextButton className="h-6" onClick={() => setNutrientLabelOpen(true)} />
        )}

        {isPremium && <p className="ml-auto text-primary">+ {medusaPriceAmountToMoney(premium_upcharge)}</p>}

        <h5 className="meal-card__title w-full mt-0">{title}</h5>
        {description && <p className="meal-card__description line-clamp-2 mt-0 mb-4">{description}</p>}

        <div className="mt-auto w-full">
          <hr />

          <dl className="meal-card__nutrition-info flex flex-wrap justify-between my-4 w-full">
            <div className="col-span-1 flex items-baseline">
              <dd className="mr-2 font-light text-base">
                {dietary_information?.nutrients.calories ? Math.round(dietary_information?.nutrients.calories) : 'XXX'}
              </dd>
              <dt className="font-normal text-xxs">Calories</dt>
            </div>
            <div className="col-span-1 flex items-baseline">
              <dd className="mr-2 font-light text-base">
                {dietary_information?.nutrients.carbohydrate
                  ? Math.round(dietary_information?.nutrients.carbohydrate)
                  : 'XX'}
                g
              </dd>
              <dt className="font-normal text-xxs">Carbs</dt>
            </div>
            <div className="col-span-1 flex items-baseline">
              <dd className="mr-2 font-light text-base">
                {dietary_information?.nutrients.protein ? Math.round(dietary_information?.nutrients.protein) : 'XX'}g
              </dd>
              <dt className="font-normal text-xxs">Protein</dt>
            </div>
          </dl>

          <hr />

          <CardActions className="mt-5">{actions}</CardActions>
        </div>
      </CardContent>

      <NutrientLabelModal
        closeButton={false}
        isOpen={nutrientLabelOpen}
        meal={meal}
        onAfterClose={() => setNutrientLabelOpen(false)}
        onProductVariantUpdate={onProductVariantUpdate}
        setIsOpen={setNutrientLabelOpen}
      />
    </Card>
  );
};
